import { Injectable } from '@angular/core';
import { RecordingFileStateEnum } from '@app/core/helpers/enums/enums';
import { isArrayNotEmpty } from '@app/core/helpers/utils';
import { RecChunkModel, RecFileModel } from '@app/modules/shared/models/db.model';
import { CommonService } from '../common.service';
import { LoggerService } from '../logger.service';
import { BaseRepository } from './base.repository';
import { IIdbError } from './dto/custom-error.dto';

@Injectable({
    providedIn: 'root'
})

export class RecIndexDbService extends BaseRepository {



    constructor(
        protected readonly logger: LoggerService,
        private readonly commonService: CommonService
    ) {
        super(logger);
    }



    /**
     * put record in recFile table
     * @param id 
     * @param name 
     * @param uploadId 
     * @param rid 
     * @param multipart 
     */
    async putFile(id: number, name: string, uploadId: string, rid: string,
        multipart: string,
        confereceId: string, state: RecordingFileStateEnum) {
        try {
            const putObj: RecFileModel = {
                id: id,
                name: name,
                uploadId: uploadId,
                uploadedPart: 0,
                createOn: new Date(),
                totalUpPart: 0,
                rid: rid,
                multipartMap: multipart,
                updatedOn: new Date(),
                confereceId,
                state
            }
            await this.db.put(this.recFileTbl, putObj);
        } catch (error) {
            this.logger.error('putFile_error', error);
            throw error;
        }
    }

    /**
     * put file chunk to recChunk table
     * @param recFileId 
     * @param part 
     * @param body 
     * @param partGroupNo 
     */
    async putFileChunk(recFileId: number, part: number, body: string | ArrayBuffer, partGroupNo: number) {
        try {
            await this.db.put(this.recChunkTbl, {
                recFileId: recFileId,
                partGroupNo: partGroupNo,
                part: part,
                body: body,
                createOn: new Date()
            });
            try {
                let chunks = await this.getFileChunks(recFileId, partGroupNo);
                if (isArrayNotEmpty(chunks)) {
                    let chunk = chunks[0]
                    return recFileId === chunk.recFileId && partGroupNo === chunk.partGroupNo
                }
                else {
                    throw 'DATA_NOT_SAVED_IDB'
                }
            } catch (error) {
                throw error;
            }
        } catch (error) {
            throw error;
        }
    }

    /**
     * get chunks of file
     * @param fileId 
     * @param uploadPartNo 
     * @returns Promise<RecChunkModel[]>
     * @throw IdbError
     * {error: 
     *  Error: Failed to execute 'bound' on 'IDBKeyRange': The parameter is not a valid key.
     * }
     */
    async getFileChunks(fileId: number, uploadPartNo: number): Promise<RecChunkModel[]> {
        try {
            return this.db.getAll(this.recChunkTbl, IDBKeyRange.bound([fileId, uploadPartNo], [fileId, uploadPartNo + 1]));
        } catch (error) {
            throw error as IIdbError;
        }
    }

    /**
     * remove recording file form indexdb
     * @param name 
     */
    async removeRecFileDb(filename: string) {
        try {
            await this.removeFileChunks(filename);
            console.log('fileChunks Removed');
            await this.db.delete(this.recFileTbl, filename);
            console.log('file is removed')
            return true;
        } catch (error) {
            this.logger.error('removeRecFileDb', error);
        }
    }

    /**
     * remove all file chunks by filename
     * @param filename 
     */
    async removeFileChunks(filename: string) {
        try {
            const file = await this.getRecFile(filename);
            if (!file) return;
            const fileId = file.id
            console.log('file to del', fileId);
            await this.db.delete(this.recChunkTbl, IDBKeyRange.bound([fileId], [fileId + 1]));
            console.log('file chuck removed');
        } catch (error) {
            this.logger.error('removeFileChunks', error);
        }
    }


    /**
     * get list of files
     */
    async getRecFiles(): Promise<RecFileModel[]> {
        try {
            return await this.db.getAll(this.recFileTbl);
        } catch (error) {
            this.logger.error('getRecFiles', error);
            throw error;
        }
    }

    /**
     * remove item from index db
     * @param fileId 
     * @param uploadPartNo 
     */
    async removeItemIDBByComparing(fileId: number, uploadPartNo: number) {
        try {
            this.logger.log(`removing items from idb fileID: ${fileId}, Group/part no : ${uploadPartNo}`)
            if (fileId && uploadPartNo) {
                const chunks = await this.getFileChunks(fileId, uploadPartNo);
                if (isArrayNotEmpty(chunks)) {
                    for (let chunk of chunks) {
                        if (chunk.partGroupNo === uploadPartNo && chunk.recFileId === fileId) {
                            let key = [chunk.recFileId, chunk.partGroupNo, chunk.part]
                            await this.db.delete(this.recChunkTbl, IDBKeyRange.bound(key, key))
                        }
                    }
                    this.logger.log(`Item Removed: GroupNo ${uploadPartNo} Parts ${chunks.length}`)
                }
                else {
                    this.logger.log(`no items exist in idb to remove: ${fileId}, Group/part no : ${uploadPartNo}`)
                }
            }
        } catch (error) {
            this.logger.error('removeItemIDB', error)
        }
    }


    /**
     * remove item from idb
     * @param fileId 
     * @param uploadPartNo 
     * @returns 
     */
    async removeItemIDB(fileId: number, uploadPartNo: number) {
        try {
            this.logger.log(`removing items from idb fileID: ${fileId}, Group/part no : ${uploadPartNo}`)
            if (fileId && uploadPartNo) {
                await this.db.delete(this.recChunkTbl, IDBKeyRange.bound([fileId, uploadPartNo], [fileId, uploadPartNo + 1]))
                return true;
            }
        } catch (error) {
            this.logger.error('removeItemIDB', error);
            throw error;
        }
    }



    async getRecFile(fileName: string): Promise<RecFileModel> {
        try {
            return await this.db.get(this.recFileTbl, IDBKeyRange.only(fileName))
        } catch (error) {
            this.logger.error('getRecFile', error);
            throw error;
        }
    }


    async getRecFilesByConferenceId(conferenceId: string): Promise<RecFileModel[]> {
        try {
            const files = await this.getRecFiles();
            return files.filter(f => f.confereceId === conferenceId);
        } catch (error) {
            this.logger.error('getRecFilesBy', error);
            throw error;
        }
    }



    /**
     * check next part no form idb
     * @param fileId 
     * @param currentUpload 
     */
    async checkNextPartNoIDB(fileId: number, currentUpload: number) {
        try {
            let res = await this.getFileChunks(fileId, currentUpload + 1)
            return res && res.length > 0 ? true : false;
        } catch (error) {
            this.logger.error('checkNextPartNoIDB', error);
            throw error;
        }
    }

    async checkUploadingPartNoIDB(fileId: number, currentUpload: number) {
        try {
            let res = await this.getFileChunks(fileId, currentUpload)
            return res && res.length > 0 ? true : false;
        } catch (error) {
            this.logger.error('checkNextPartNoIDB', error);
            throw error;
        }
    }


    /**
     * get meta of rec file from idb
     * @param name 
     */
    async getMetaOfFileIDB(name: string): Promise<RecFileModel> {
        try {
            return await this.db.get(this.recFileTbl, name);
        } catch (error) {
            this.logger.error('getMetaOfFileIDB', error);
            throw error;
        }
    }

    /**
     * get last id from rec file table
     */
    async getLastIdOfRecFile(): Promise<number> {
        try {
            let data = await this.db.getAll(this.recFileTbl)
            if (data) {
                data = data.sort((a, b) => b.createOn.getTime() - a.createOn.getTime());
                return data.length > 0 ? data[0].id : 0
            }
            return 0
        } catch (error) {
            this.logger.error('getLastIdOfRecFile', error);
            throw error;
        }
    }

    /**
     * update total upload part in db
     * @param name 
     * @param totalNo 
     */
    async updatetotalUploadPart(name: string, totalNo: number) {
        try {
            let recFileO = await this.getRecFile(name);
            if (recFileO) {
                recFileO.totalUpPart = totalNo;
                await this.db.put(this.recFileTbl, recFileO)
            }
        } catch (error) {
            this.logger.error('updatetotalUploadPart', error);
            throw error;
        }
    }


    // async updateRecFileIsEnded(name: string) {
    //     try {
    //         let recFileO = await this.getRecFile(name);
    //         if (recFileO) {
    //             recFileO.isEnded = true;
    //             await this.db.put(this.recFileTbl, recFileO)
    //         }
    //     } catch (error) {
    //         this.logger.error('updateRecFileIsEnded', error);
    //         throw error;
    //     }
    // }




    async updateRecFileState(name: string, state: RecordingFileStateEnum) {
        try {
            let recFileO = await this.getRecFile(name);
            if (recFileO) {
                recFileO.state = state;
                await this.db.put(this.recFileTbl, recFileO)
            }
        } catch (error) {
            this.logger.error('updateRecFileIsEnded', error);
            throw error;
        }
    }




    async updateAllRecFileStateToStopped() {
        try {
            let recFileO = await this.getRecFiles();
            for (let file of recFileO) {
                file.state = RecordingFileStateEnum.EndedData;
                await this.db.put(this.recFileTbl, file)
            }
        } catch (error) {
            this.logger.error('updateAllRecFileIsEnded', error);
        }
    }


    /**
     * this method update the status of local recording from pending to Complete
     * this method is called after sucessfully completion of uploading recording to s3
     * this is last step in processing of recording once completed user redirect to dashboard/manage session w.r.t role
     * @param sessionRecId session recording id
     */
    async updateUploadedPart(name: string, uploadedPart: number, multipart?: string) {
        try {
            let recFileO = await this.getRecFile(name);
            if (recFileO) {
                recFileO.uploadedPart = uploadedPart;
                recFileO.multipartMap = multipart;
                recFileO.updatedOn = new Date();
                await this.db.put(this.recFileTbl, recFileO)
            }
        } catch (error) {
            this.logger.error('updateUploadedPart', error);
            throw error;
        }
    }


    async updateRecMeta(name: string, recordingId: string, uploadId: string) {
        try {
            let recFileO = await this.getRecFile(name);
            if (recFileO) {
                recFileO.uploadId = uploadId;
                recFileO.rid = recordingId;
                await this.db.put(this.recFileTbl, recFileO)
            }
        } catch (error) {
            throw error;
        }
    }

    close(){
        this.db.close();
    }

}